import {
  AnalyticsQueue,
  LoaderOptions,
  PostMessageEvent,
  createAppInitializedBeacon,
  createV2BeaconGenerator,
  getIntegrationType,
  getSiteDomain,
  getUserKey,
  logUnknownError,
  Tags,
  Logger,
  generateUUID,
  setSessionId,
  createBlockedTrafficBeacon,
} from '@flipp/shopper-ad-web-shared';

import { shouldBlockBotTraffic } from './shouldBlockBotTraffic';
import { calculateGeomData } from './geom';
import { overrideOptions } from './options';
import SlotManager from './SlotManager';
import { MockedSlot } from './Slot';
import { observeSPA } from './singlePageApp';

try {
  // eslint-disable-next-line functional/no-let
  let isBot = false;

  type Callback = () => void | Promise<void>;
  const executeCallback = async (callback: Callback): Promise<void> => {
    if (typeof callback === 'function') {
      isBot = await shouldBlockBotTraffic();
      void callback();
    }
  };

  // eslint-disable-next-line functional/no-let
  let firedInit = false;

  // Publisher Tag Interface
  window.flippxp = window.flippxp || {};
  window.flippxp.evTag = window.flippxp.evTag || {};
  window.flippxp.run = window.flippxp.run || [];
  window.flippxp.debugMode = window.flippxp.debugMode || {};
  window.flippxp.calculateGeomData = calculateGeomData;
  window.flippxp.overrideOptions = overrideOptions;
  window.flippxp.vimpTrackerFunc =
    window.flippxp.vimpTrackerFunc ||
    (() => {
      if (window !== window.parent) {
        window.parent.postMessage({ type: PostMessageEvent.VIMP }, '*');
      }
    });
  window.flippxp.cookieSynced = window.flippxp.cookieSynced || false;
  window.flippxp.registerSlot = function (
    targetSelector: string,
    publisherNameIdentifier: string,
    siteId: number,
    zoneIds: readonly number[],
    options: LoaderOptions = {},
  ) {
    const beaconData = {
      partnerNameIdentifier: publisherNameIdentifier,
      siteId,
      zoneId: zoneIds[0] || 0,
      options,
      postalCode: window.flippxp.geo?.postal_code,
      country: window.flippxp.geo?.country,
    };
    // If we haven't fired the init event yet then do it now
    if (!firedInit) {
      const sessionIdFromUrl = new URLSearchParams(window.location.search).get(
        'sessionId',
      );
      setSessionId(
        sessionIdFromUrl ??
          (typeof options.sessionId === 'string' &&
          options.sessionId.trim() !== ''
            ? options.sessionId
            : undefined) ??
          generateUUID(),
      );
      AnalyticsQueue.prodAnalytics = !!window.flippxp.debugMode?.prodAnalytics;
      firedInit = true;
      const v1AppInitializedBeacon = createAppInitializedBeacon(beaconData);
      AnalyticsQueue.enqueue(v1AppInitializedBeacon);
      Logger.debug('Analytics sendAppInitialized');

      const v2BeaconGenerator = createV2BeaconGenerator(() => ({
        v2SchemaEndpoint: '__v2SchemaEndpoint',
        signingKey:
          ('__v2SigningKey' as string) === 'undefined'
            ? undefined
            : '__v2SigningKey',
        site: getSiteDomain(options.overrideHostname),
        country: window.flippxp.geo?.country,
        userPostalCode: window.flippxp.geo?.postal_code,
        uid: getUserKey(options) ?? undefined,
        integrationType: getIntegrationType(options),
        startCompact: !!options.startCompact,
        fixedHeightUnit: !!options.fixedHeightUnit,
        mobileSwipe: !!(
          options.mobileSwipe ?? window.flippxp.settings?.mobileSwipe
        ),
        appVersion: '__appVersion',
      }));
      v2BeaconGenerator.sendAppInitialized(
        v1AppInitializedBeacon.shopperPlatformBase.uuid,
      );
      Logger.debug('Analytics V2 sendAppInitialized)');
    }
    Tags.reset();
    if (siteId === 1179443) {
      const el = document.getElementById('flyerWidgetLabel');
      if (el) {
        el.style.display = 'none';
      }
    }

    if (isBot) {
      AnalyticsQueue.enqueue(createBlockedTrafficBeacon(beaconData));
      Logger.debug('Analytics sendCreateBlockedTrafficBeacon');
      return new MockedSlot();
    }

    if (options.singlePageApp ?? window.flippxp.settings?.singlePageApp) {
      observeSPA({
        targetSelector,
        publisherNameIdentifier,
        siteId,
        zoneIds,
        options,
      });
    }

    window.flippxp.slotManager =
      window.flippxp.slotManager || new SlotManager();
    return window.flippxp.slotManager.register(
      targetSelector,
      publisherNameIdentifier,
      siteId,
      zoneIds || [],
      window.flippxp.overrideOptions(options),
    );
  };

  const oldQueue: Callback[] = Array.isArray(window.flippxp.run)
    ? window.flippxp.run
    : [];

  window.flippxp.run = {
    push: (callback: () => void) => {
      void executeCallback(callback);
      return null;
    },
    shift: () => {
      return () => null;
    },
    length: 0,
  };

  for (const callback of oldQueue) {
    void executeCallback(callback);
  }
} catch (e) {
  logUnknownError(e);
}
